import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@material-ui/core'
import { useState } from 'react'
import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { AddressFields, formatErrors } from 'shared/components/Address/AddressFields'
import { Button } from 'shared/components/Button/Button'
import { DateInput } from 'shared/components/DateInput/DateInput'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { SSNInput } from 'shared/components/MaskedInput/SSNInput'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { getDropdownStates } from 'shared/helpers/states'
import { Handler, HandlerOf } from 'shared/helpers/typeHelper'
import { useScrollToErrors } from 'shared/hooks/useScrollToErrors'
import { updateUserInfo, UpdateUserPayload, User } from 'shared/models/user'
import { sizes } from 'shared/theme'
import { styled } from 'styled-components'

import { ErrorMessage, ErrorMessageList } from '../../../../components/Message/ErrorMessage'
import { formatErrorsForBanner } from '../../../../forms'
import { useUserProvider } from '../../../Context/UserContext'
import {
  CDLForm,
  CDLSchema,
  getCreateAddressBody,
  getCreateCDLApplicationDriverBody,
  getInitialForm,
} from './CDLInformationParser.constants'

interface CDLInformationManualProps {
  nextStep: Handler
  parsedCDL: CDLForm | null
  setParsedCDL: HandlerOf<CDLForm | null>
  user: User
}

export function CDLInformationManual({ nextStep, parsedCDL, setParsedCDL, user }: CDLInformationManualProps) {
  const [loading, setLoading] = useState(false)
  const { showBanner, clearUserCache } = useUserProvider()

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CDLForm>({
    mode: 'onSubmit',
    defaultValues: getInitialForm(parsedCDL, user),
    resolver: yupResolver(CDLSchema),
  })

  useScrollToErrors(errors)

  const updateDriverMutation = useMutation(
    (payload: UpdateUserPayload) => updateUserInfo(user.id, payload, { vgs: true, v3Api: true }),
    {
      onError: (err: any) => {
        showBanner('error', formatErrorsForBanner(err))
        setLoading(false)
      },
    }
  )

  const onDataSubmit = async (formData: CDLForm) => {
    const driverPayload = getCreateCDLApplicationDriverBody(formData as Required<CDLForm>)
    const addressPayload = getCreateAddressBody(formData as Required<CDLForm>)
    await updateDriverMutation.mutateAsync({
      first_name: formData.firstName,
      middle_name: formData.middleName ?? null,
      last_name: formData.lastName,
      driver: driverPayload,
      shipping_address: addressPayload,
      cdl_update: true,
    })
    await clearUserCache()
    setParsedCDL(null) // We already saved changes from cdl, and now we want user details to be source of truth
  }

  const onSubmit = async (formData: CDLForm) => {
    try {
      setLoading(true)
      await onDataSubmit(formData)
      setLoading(false)
      nextStep()
    } catch {
      setLoading(false)
    }
  }

  function maybeRenderErrors() {
    const licenseNumber = parsedCDL?.licenseNumber || user.driver?.licenseNumber

    if (licenseNumber) return null

    return (
      <>
        <ErrorMessage>
          <Text variant="header4">Please complete the missing information:</Text>
          <ErrorMessageList>
            {!licenseNumber ? (
              <li>
                <Text variant="paragraph2">License Number</Text>
              </li>
            ) : null}
          </ErrorMessageList>
        </ErrorMessage>
        <Box m={4} />
      </>
    )
  }

  return (
    <Container>
      <Text variant="header2" align="center">
        Add your personal info
      </Text>
      <Box mt={5} />

      {maybeRenderErrors()}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          render={({ field, fieldState }) => (
            <SSNInput {...field} label="Social Security Number (SSN)" error={fieldState.error?.message} />
          )}
          name="ssn"
          control={control}
        />

        <Box mb={4} />

        <TwoFields>
          <Controller
            render={({ field, fieldState }) => (
              <TextInput {...field} label="First name" error={fieldState.error?.message} noMargin />
            )}
            name="firstName"
            control={control}
          />

          <Controller
            render={({ field, fieldState }) => (
              <TextInput {...field} label="Middle name" error={fieldState.error?.message} noMargin />
            )}
            name="middleName"
            control={control}
          />
        </TwoFields>

        <Box mb={1} />

        <Controller
          render={({ field, fieldState }) => (
            <TextInput {...field} label="Last name" error={fieldState.error?.message} />
          )}
          name="lastName"
          control={control}
        />

        <Box mb={4} />

        <Controller
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              label="Date of Birth"
              error={fieldState.error?.message}
              shouldNotForwardRef
            />
          )}
          name="dob"
          control={control}
        />

        <Box mb={4} />

        <TwoFields>
          <Controller
            render={({ field, fieldState }) => (
              <Dropdown
                {...field}
                isFullWidth
                error={Boolean(fieldState.error?.message)}
                options={getDropdownStates()}
                label="License State"
                staticLabel="License State"
              />
            )}
            name="licenseState"
            control={control}
          />

          <Controller
            render={({ field, fieldState }) => (
              <TextInput {...field} label="CDL Number" error={fieldState.error?.message} />
            )}
            name="licenseNumber"
            control={control}
          />
        </TwoFields>

        <Box mb={1} />

        <TwoFields>
          <Controller
            render={({ field, fieldState }) => (
              <DateInput
                {...field}
                label="Issue Date"
                error={fieldState.error?.message}
                shouldNotForwardRef
              />
            )}
            name="licenseIssued"
            control={control}
          />
          <Controller
            render={({ field, fieldState }) => (
              <DateInput
                {...field}
                label="Expiration Date"
                error={fieldState.error?.message}
                shouldNotForwardRef
              />
            )}
            name="licenseExpiration"
            control={control}
          />
        </TwoFields>

        <Text variant="header4" mt={2} mb={0.5} align="left" styles="width: 100%;">
          Mailing address
        </Text>

        <Controller
          render={({ field, fieldState }) => (
            <AddressFields {...field} errors={formatErrors(fieldState.error)} />
          )}
          name="address"
          control={control}
        />

        <Box mt={4.5} />

        <Button type="submit" size={sizes.extraLarge} isLoading={loading} margin="0 auto">
          Continue
        </Button>
      </form>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 100%;
  width: 100%;
`

const TwoFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 8px;

  @media (max-width: 460px) {
    grid-template-columns: 1fr;
    grid-row-gap: 8px;
  }
`
